<script setup lang="ts">
import { AbcTable } from "@ordbogen-npm/abc-dsk";
import { storeToRefs } from "pinia";
import { capitalize, computed } from "vue";

import { useArticleFont } from "@/common/composables/use-article-font.composable";
import { LEVEL_INTERMEDIATE } from "@/common/level-types";
import InflectionSchemeCell from "@/components/article/dictsys/inflectionSchemes/InflectionSchemeCell.vue";
import { buildComparisonLabel } from "@/components/article/dictsys/inflectionSchemes/labels";
import { makeInflectionCell } from "@/helpers/dictsys-inflection-scheme/inflection-scheme.helper";
import { useSettingsStore } from "@/stores";
import {
  DictsysEnglishComparisons,
  type DictsysInflectionSchemeEnglishAdjectiveComparisons
} from "@/types";
import type { InflectionTable } from "@/types/inflection-scheme.type";

const props = defineProps<{
  inflectionScheme: DictsysInflectionSchemeEnglishAdjectiveComparisons;
}>();

const { activeWordClassLevel: wordClassLevel } = storeToRefs(useSettingsStore());

const columns = computed<InflectionTable["columns"]>(() =>
  DictsysEnglishComparisons.map((comparison) => ({
    value: capitalize(
      buildComparisonLabel(
        wordClassLevel.value,
        comparison,
        wordClassLevel.value === LEVEL_INTERMEDIATE ? "<br/>" : " "
      )
    )
  }))
);

const rows = computed<InflectionTable["rows"]>(() => [
  {
    cells: DictsysEnglishComparisons.map((comparison) => {
      const cell = props.inflectionScheme[comparison][0];
      return makeInflectionCell(cell.inflexion, cell.prefix, cell.suffix);
    })
  }
]);

const { articleFontClass } = useArticleFont();
</script>

<template>
  <AbcTable :columns="columns" :rows="rows">
    <template #head-cell="{ cell }">
      <!-- eslint-disable-next-line vue/no-v-html - needed for new-lines -->
      <span :class="articleFontClass" v-html="cell"></span>
    </template>
    <template #body-cell="{ cell }">
      <InflectionSchemeCell :content="cell" />
    </template>
  </AbcTable>
</template>
